import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';

import ArrowBottom from 'crehana-icons/ArrowBottom';
import ArrowTop from 'crehana-icons/ArrowTop';
import { baseLighter } from 'crehana-ui/styles/colors';
import useCollapse from 'Jsx/Shared/Hooks/useCollapse';
import matchMediaQuery from 'Utils/matchMediaQuery';
import StringParser from 'Jsx/Shared/StringParser';
import locales from '../../i18n/localeEs';

import BulletCheck from '../../components/BulletCheck';

const translations = locales.es;

type RequirementsDetailType = {
  text: string;
  title: string;
};

type RequirementsProps = {
  text: string;
  requirementDetails: RequirementsDetailType[];
  isFirstElement?: boolean;
};

const Requirements: FC<RequirementsProps> = ({
  text,
  requirementDetails,
  isFirstElement,
}) => {
  const { t } = useTranslation();
  const [isClientAndViewportWidth, setIsClientAndViewportWidth] = useState({
    isClient: false,
    isTablet: false,
  });
  const elemCollapse = React.useRef<HTMLDivElement | null>(null);
  const heightElem = elemCollapse.current?.offsetHeight || 50;

  useEffect(() => {
    // track course detail v3
    if (!requirementDetails && window.Sentry) {
      window.Sentry.captureMessage('CourseDetail V3 / Requirement component');
      window.Sentry.setTag('course_detail_v3', 'requirement_component');
      window.Sentry.setTag('course_detail_v3_referrer', document.referrer);
    }
    setIsClientAndViewportWidth({
      isClient: true,
      isTablet: matchMediaQuery('md').matches,
    });
  }, []);

  const { getToggleProps, getCollapseProps, isOpen } = useCollapse({
    collapsedHeight: isClientAndViewportWidth ? 72 : 300,
  });

  const renderRequirement = () =>
    requirementDetails?.map((requirement, index) => (
      <React.Fragment key={index.toString()}>
        {requirement.text && requirement.title && (
          <>
            <h3 className="font-subtitle2 mb-8 font-bold">
              {requirement.title}
            </h3>
            <div ref={elemCollapse}>
              <ul className="mb-16">
                <BulletCheck
                  key={index.toString()}
                  paragraph={requirement.text}
                />
              </ul>
            </div>
          </>
        )}
        {requirement.text &&
          !requirement.title &&
          requirementDetails.length <= 1 && (
            <p ref={elemCollapse} className="font-body2 ml-4">
              <StringParser string={requirement.text} breaks style link>
                {({ string }) => string}
              </StringParser>
            </p>
          )}
        {requirement.title &&
          !requirement.text &&
          requirementDetails.length >= 2 && (
            <div ref={elemCollapse}>
              <ul className="mb-16">
                <BulletCheck
                  key={index.toString()}
                  paragraph={requirement.title}
                />
              </ul>
            </div>
          )}
      </React.Fragment>
    ));

  const ToggleContent = (
    <>
      <div {...getCollapseProps()}>{renderRequirement()}</div>
      <button
        type="button"
        className="mt-20 font-gil font-bold text-base-lighter"
        {...getToggleProps()}
      >
        {!isOpen ? (
          <p className="flex items-center font-bold">
            <span className="mr-8">
              {t('SHOW_MORE', {
                defaultValue: translations.SHOW_MORE,
              })}
            </span>
            <ArrowBottom size={14} color={baseLighter} />
          </p>
        ) : (
          <p>
            {t('SHOW_LESS', {
              defaultValue: translations.SHOW_LESS,
            })}{' '}
            <ArrowTop size={8} color={baseLighter} />
          </p>
        )}
      </button>
    </>
  );

  if (!requirementDetails) {
    return null;
  }

  return (
    <section
      className={`py-16 lg:py-32 border-b border-solid border-gray-light mb-16 lg:mt-40 ${
        isFirstElement ? 'border-t md:p-32 md:border' : ''
      }`}
    >
      <h2 className="font-h3 font-h4-md mb-24 font-bold">{text}</h2>
      {isFirstElement || heightElem < 300 ? renderRequirement() : ToggleContent}
    </section>
  );
};

export default Requirements;
